import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useLang } from "../lib/i18n";
import { TypeIcon, Avatar, StatusPill } from "../components/work/workMeta";
import { Skeleton } from "../components/ui/skeleton";

function Stat({ label, value, testId }) {
  return <div className="apha-card p-4" data-testid={testId}><div className="text-xs text-muted-foreground">{label}</div><div className="font-heading text-2xl font-semibold mt-1">{value}</div></div>;
}

export default function SprintReport() {
  const { sprintId } = useParams();
  const { t } = useLang();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/sprints/${sprintId}/report`).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e))); }, [sprintId]);
  if (!data) return <Skeleton className="h-96 w-full" />;
  const { sprint, stats, items, scope_changes, burndown } = data;
  const m = sprint.completed_metrics;
  return (
    <div className="space-y-6" data-testid="sprint-report-page">
      <button onClick={() => navigate(`/projects/${sprint.project_id}/backlog`)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" />{t("backlog")}</button>
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">{sprint.name} <span className="text-base text-muted-foreground font-normal">· {sprint.status}</span></h1>
        <p className="text-sm text-muted-foreground mt-1">{sprint.start_date || "—"} → {sprint.end_date || "—"}{sprint.goal ? ` · “${sprint.goal}”` : ""}</p>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label={`${t("committed")} (${t("points")})`} value={sprint.commitment_snapshot ? sprint.commitment_snapshot.points : "—"} testId="stat-committed" />
        <Stat label={`${t("done")} (${t("points")})`} value={`${stats.done_points} / ${stats.points}`} testId="stat-done" />
        <Stat label={t("items")} value={`${stats.done_count} / ${stats.item_count}`} testId="stat-items" />
        <Stat label={t("remainingHours")} value={`${stats.remaining_hours} / ${stats.estimate_hours}`} testId="stat-hours" />
      </div>
      {m && (
        <div className="apha-card p-4 text-sm" data-testid="completed-metrics">
          <div className="font-heading font-semibold mb-2">{t("completedSprint")}</div>
          <div className="grid sm:grid-cols-3 gap-2 text-muted-foreground">
            <div>{t("committed")}: {m.committed_count} {t("items")} · {m.committed_points} {t("points")}</div>
            <div>{t("done")}: {m.completed_count} {t("items")} · {m.completed_points} {t("points")} ({m.completion_pct ?? "—"}% of commitment)</div>
            <div>{t("scopeChanges")}: +{m.added_count} / −{m.removed_count} · unfinished {m.unfinished_count}</div>
          </div>
        </div>
      )}
      {burndown && (
        <div className="apha-card p-4" data-testid="sprint-burndown">
          <div className="font-heading font-semibold mb-2">{t("burndown")} <span className="text-xs text-muted-foreground font-normal">({burndown.unit})</span></div>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={burndown.series}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip />
              <Line type="monotone" dataKey="ideal" stroke="#94A3B8" strokeDasharray="4 4" dot={false} name="Ideal" />
              <Line type="monotone" dataKey="remaining" stroke="#1E3A8A" strokeWidth={2} name="Remaining" connectNulls={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
      <div className="grid lg:grid-cols-[1fr_320px] gap-6">
        <section className="apha-card" data-testid="sprint-items">
          <div className="px-4 py-2.5 border-b font-heading font-semibold">{t("workItems")} ({items.length})</div>
          <div className="divide-y">
            {items.map((i) => (
              <Link key={i.id} to={`/work/${i.key}`} className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-secondary/50" data-testid={`sprint-item-${i.key}`}>
                <TypeIcon type={i.type} /><span className="text-xs text-muted-foreground w-16">{i.key}</span><span className="flex-1 truncate">{i.title}</span>
                {!i.committed && <span className="text-[10px] font-semibold text-[#9A3412] bg-[#FFEDD5] border border-[#FED7AA] rounded px-1">ADDED</span>}
                <StatusPill name={i.status_name} category={i.status_category} />
                {i.story_points != null && <span className="text-[10px] font-semibold bg-secondary rounded-full px-1.5">{i.story_points}</span>}
                <Avatar name={i.assignee_name} />
              </Link>
            ))}
          </div>
        </section>
        <section className="apha-card" data-testid="scope-changes">
          <div className="px-4 py-2.5 border-b font-heading font-semibold">{t("scopeChanges")} ({scope_changes.length})</div>
          <div className="divide-y text-sm">
            {scope_changes.map((c, i) => (
              <div key={i} className="px-3 py-1.5 flex items-center gap-2">
                <span className={`text-[10px] font-semibold rounded px-1 ${c.kind === "ADDED" ? "text-[#166534] bg-[#DCFCE7]" : "text-[#991B1B] bg-[#FEE2E2]"}`}>{c.kind}</span>
                <Link to={`/work/${c.key}`} className="text-xs hover:underline">{c.key}</Link>
                <span className="text-xs text-muted-foreground ml-auto">{c.actor_name} · {c.at.slice(0, 10)}</span>
              </div>
            ))}
            {scope_changes.length === 0 && <div className="px-3 py-3 text-xs text-muted-foreground">{t("noData")}</div>}
          </div>
        </section>
      </div>
    </div>
  );
}
